import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  ChartLine, Users, Receipt, Wallet, Star, Check, 
  WhatsappLogo, Crown, Lightning, Shield, Sparkle,
  CheckCircle, ArrowRight, Gift, TrendUp, ClipboardText, Bell, Eye, EyeSlash
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const LandingPage = () => {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const [signupStep, setSignupStep] = useState(1); // Step 1: Name, Profile ID, Mobile | Step 2: Email, City, Password
  const [signupForm, setSignupForm] = useState({ 
    name: '', 
    profile_id: '',
    email: '',
    mobile: '', 
    location: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [profileIdAvailable, setProfileIdAvailable] = useState(null);
  const [checkingProfileId, setCheckingProfileId] = useState(false);

  // Check profile ID availability
  const checkProfileId = async (profileId) => {
    if (!profileId || profileId.length < 3) {
      setProfileIdAvailable(null);
      return;
    }
    
    setCheckingProfileId(true);
    try {
      const response = await axios.get(`${API_URL}/auth/check-profile-id/${profileId}`);
      setProfileIdAvailable(response.data.available);
    } catch (error) {
      console.error('Profile ID check error:', error);
      setProfileIdAvailable(null);
    } finally {
      setCheckingProfileId(false);
    }
  };

  // Handle Step 1 - Validate and go to Step 2
  const handleStep1Submit = (e) => {
    e.preventDefault();
    
    if (!signupForm.name.trim()) {
      toast.error('कृपया तुमचं नाव टाका');
      return;
    }
    
    if (!signupForm.profile_id || signupForm.profile_id.length < 3) {
      toast.error('Profile ID किमान 3 characters असावी');
      return;
    }
    
    if (profileIdAvailable === false) {
      toast.error('कृपया वेगळा Profile ID निवडा');
      return;
    }
    
    if (profileIdAvailable === null && signupForm.profile_id.length >= 3) {
      toast.error('कृपया Profile ID availability check होऊ द्या');
      return;
    }
    
    if (!signupForm.mobile.trim() || signupForm.mobile.length < 10) {
      toast.error('कृपया valid Mobile number टाका');
      return;
    }
    
    // Go to step 2
    setSignupStep(2);
  };

  // Handle Step 2 - Final signup
  const handleSignupSubmit = async (e) => {
    e.preventDefault();
    
    if (!signupForm.email.trim()) {
      toast.error('कृपया Email टाका');
      return;
    }
    
    if (!signupForm.location.trim()) {
      toast.error('कृपया शहर टाका');
      return;
    }
    
    if (!signupForm.password || signupForm.password.length < 6) {
      toast.error('Password किमान 6 characters असावा');
      return;
    }
    
    setLoading(true);
    
    try {
      // Use AuthContext signup function for proper state management
      await signup(
        signupForm.name,
        signupForm.email,
        signupForm.password,
        signupForm.mobile,
        signupForm.location,
        signupForm.profile_id
      );
      
      toast.success('🎉 अभिनंदन! तुमचं account तयार झालं!');
      navigate('/dashboard');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Signup failed. पुन्हा try करा.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: Users,
      title: 'Enquiry Management',
      description: 'सर्व leads एका ठिकाणी track करा. कधीही lead हरवू नका!'
    },
    {
      icon: Receipt,
      title: 'Professional Invoices',
      description: 'WhatsApp वर direct invoice share करा. Payment लवकर मिळवा!'
    },
    {
      icon: Wallet,
      title: 'Payment Tracking',
      description: 'Pending payments track करा. Automatic reminders पाठवा!'
    },
    {
      icon: ChartLine,
      title: 'Business Analytics',
      description: 'Revenue, customers, conversion - सगळं एका dashboard वर!'
    }
  ];

  const problems = [
    { title: 'Clients नाहीत मिळत?', desc: 'Skills आहेत पण clients येत नाहीत?' },
    { title: 'Pricing कशी ठरवायची?', desc: 'काम सस्त्यात करून देता का?' },
    { title: 'Payment issues?', desc: 'काम पूर्ण पण payment येत नाही?' },
    { title: 'Enquiry हरवतात?', desc: 'WhatsApp मध्ये गोंधळ होतो?' }
  ];

  const testimonials = [
    {
      name: 'प्रिया शर्मा',
      business: 'Freelance Designer, Mumbai',
      text: 'Designbiz.in ने माझं काम खूप सोपं केलं! आता मी design वर focus करते, बाकी सगळं automatically व्यवस्थित होतं.',
      result: '3x Revenue वाढ'
    },
    {
      name: 'अमित पाटील',
      business: 'Flex & Signage Pro, Pune',
      text: 'Enquiry track करणं आणि invoices पाठवणं इतकं सोपं झालं की माझा वेळ बचतो. Highly recommended!',
      result: '60% वेळ बचत'
    },
    {
      name: 'स्नेहा देसाई',
      business: 'Creative Studio, Nashik',
      text: 'WhatsApp integration सगळ्यात best आहे. Clients ला instant invoice मिळतं आणि payment पण लवकर येतं!',
      result: 'Faster Payments'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <SEOHead 
        title="Designbiz.in - भारतातील Designer साठी #1 Business Platform | Enquiry Management, Invoices, CRM"
        description="Freelance Designers, Graphic Designers, Flex Printing साठी Complete Business Management System. Enquiry Tracking, Professional Invoices, Payment Management, WhatsApp Integration. 100% FREE मध्ये सुरू करा!"
        keywords="designbiz, designer business, graphic designer software, freelance designer tools, lead management, invoice software, CRM for designers, flex printing business, designer portfolio, business management, marathi, india, maharashtra, pune, mumbai"
        url="https://designbiz.in/"
      />
      {/* Navigation */}
      <nav className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50 backdrop-blur-lg bg-white/95">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-['Chivo'] text-2xl font-black tracking-tighter text-[#FF5A36]">
                Designbiz.in
              </h1>
              <p className="text-xs text-[#6B7280]">भारतातील Designer साठी #1 Platform</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/auth')}
                className="hidden md:inline-flex font-medium"
              >
                Login
              </Button>
              <Button
                onClick={() => navigate('/auth')}
                className="bg-[#FF5A36] hover:bg-[#E04828] text-white rounded-full font-bold px-6"
              >
                Free Profile बनवा →
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section - Dark & Impressive */}
      <div className="relative overflow-hidden bg-gradient-to-br from-gray-900 via-purple-900 to-orange-900 min-h-screen flex items-center">
        {/* Animated background patterns */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00em0wLTMwYzAtMi4yMS0xLjc5LTQtNC00cy00IDEuNzktNCA0IDEuNzkgNCA0IDQgNC0xLjc5IDQtNHpNNiAzNGMwLTIuMjEtMS43OS00LTQtNHMtNCAxLjc5LTQgNCAxLjc5IDQgNCA0IDQtMS43OSA0LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-40"></div>
        </div>

        {/* Floating elements animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
          <div className="absolute top-40 right-10 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
          <div className="absolute bottom-20 left-1/3 w-72 h-72 bg-pink-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Side - Content */}
            <div className="text-left space-y-8 animate-fade-in-left">
              <div>
                <Badge className="bg-gradient-to-r from-orange-500 to-pink-500 text-white border-0 mb-6 px-5 py-2.5 text-sm font-bold shadow-lg inline-flex items-center gap-2 animate-bounce-slow">
                  <Sparkle className="h-4 w-4 animate-pulse" weight="fill" /> 
                  100% FREE - No Credit Card Required
                </Badge>
                
                <h1 className="font-['Chivo'] text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-white mb-6 leading-[1.1]">
                  तुमची{" "}
                  <span className="bg-gradient-to-r from-orange-400 to-pink-500 bg-clip-text text-transparent animate-gradient">
                    Profile
                  </span>{" "}
                  बनवा,
                  <br />
                  आणि{" "}
                  <span className="text-orange-400">Business</span> ला{" "}
                  <br />
                  <span className="bg-gradient-to-r from-green-400 to-teal-400 bg-clip-text text-transparent">
                    Grow
                  </span>{" "}
                  करा!
                </h1>

                <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
                  तुमचा संपूर्ण business <span className="font-bold text-white">एकाच ठिकाणी</span> व्यवस्थित manage करा
                </p>

                <div className="flex flex-wrap gap-4 mb-8">
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    <CheckCircle className="h-5 w-5 text-green-400" weight="fill" />
                    <span className="text-white font-medium">Enquiry Management</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    <CheckCircle className="h-5 w-5 text-green-400" weight="fill" />
                    <span className="text-white font-medium">Smart Invoices</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    <CheckCircle className="h-5 w-5 text-green-400" weight="fill" />
                    <span className="text-white font-medium">Payment Tracking</span>
                  </div>
                  <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full border border-white/20">
                    <CheckCircle className="h-5 w-5 text-green-400" weight="fill" />
                    <span className="text-white font-medium">Analytics</span>
                  </div>
                </div>

                {/* Trust Stats */}
                <div className="grid grid-cols-3 gap-6 pt-8 border-t border-white/20">
                  <div className="text-center">
                    <div className="font-['Chivo'] text-3xl md:text-4xl font-black text-orange-400 mb-1">
                      500+
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 font-medium">Active Designers</div>
                  </div>
                  <div className="text-center">
                    <div className="font-['Chivo'] text-3xl md:text-4xl font-black text-purple-400 mb-1">
                      ₹2 Cr+
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 font-medium">Revenue</div>
                  </div>
                  <div className="text-center">
                    <div className="font-['Chivo'] text-3xl md:text-4xl font-black text-green-400 mb-1">
                      98%
                    </div>
                    <div className="text-xs md:text-sm text-gray-400 font-medium">Happy Users</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Side - Signup Form */}
            <div className="animate-fade-in-right">
              <Card className="border-2 border-orange-500 bg-white shadow-2xl relative overflow-hidden transform hover:scale-[1.02] transition-transform duration-300">
                {/* Glowing effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-orange-500/10 to-purple-500/10"></div>
                
                <div className="absolute top-0 right-0 bg-gradient-to-br from-orange-500 to-pink-600 text-white px-6 py-2 text-sm font-bold rounded-bl-2xl flex items-center gap-1 shadow-lg">
                  <Gift className="h-4 w-4" weight="fill" /> 100% FREE
                </div>
                
                <CardContent className="relative pt-10 pb-6 px-5 md:px-6">
                  <div className="text-center mb-6">
                    <h3 className="font-['Chivo'] text-xl md:text-2xl font-black text-gray-900 mb-2">
                      {signupStep === 1 ? 'Free Profile बनवा! 🎨' : 'जवळ जवळ पूर्ण! 🚀'}
                    </h3>
                    <p className="text-gray-600 text-sm">
                      {signupStep === 1 ? 'फक्त 30 seconds मध्ये सुरू करा' : 'फक्त थोडी माहिती बाकी आहे'}
                    </p>
                    {/* Step indicator */}
                    <div className="flex items-center justify-center gap-2 mt-3">
                      <div className={`w-8 h-1 rounded-full ${signupStep >= 1 ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
                      <div className={`w-8 h-1 rounded-full ${signupStep >= 2 ? 'bg-orange-500' : 'bg-gray-200'}`}></div>
                    </div>
                  </div>
                  
                  {/* Step 1: Name, Profile ID, Mobile */}
                  {signupStep === 1 && (
                    <form onSubmit={handleStep1Submit} className="space-y-4">
                      {/* Name */}
                      <div className="space-y-1">
                        <Label htmlFor="signup-name" className="text-xs font-semibold text-gray-700">
                          तुमचं नाव *
                        </Label>
                        <Input 
                          id="signup-name"
                          placeholder="उदा: प्रशांत देशमुख"
                          value={signupForm.name}
                          onChange={(e) => setSignupForm({...signupForm, name: e.target.value})}
                          required
                          className="border-2 border-gray-200 focus:border-orange-500 h-12 text-base"
                        />
                      </div>

                      {/* Profile ID */}
                      <div className="space-y-1">
                        <Label htmlFor="signup-profile-id" className="text-xs font-semibold text-gray-700">
                          Profile ID (Username) *
                        </Label>
                        <Input 
                          id="signup-profile-id"
                          placeholder="prashant_designs"
                          value={signupForm.profile_id}
                          onChange={(e) => {
                            const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
                            setSignupForm({...signupForm, profile_id: value});
                            checkProfileId(value);
                          }}
                          required
                          className={`border-2 h-12 text-base ${
                            profileIdAvailable === true ? 'border-green-500' : 
                            profileIdAvailable === false ? 'border-red-500' : 'border-gray-200'
                          } focus:border-orange-500`}
                        />
                        {signupForm.profile_id && (
                          <p className={`text-xs ${profileIdAvailable ? 'text-green-600' : 'text-red-600'}`}>
                            {checkingProfileId ? 'Checking...' : 
                             profileIdAvailable ? `✅ designbiz.in/${signupForm.profile_id}` : 
                             profileIdAvailable === false ? '❌ Already taken' : ''}
                          </p>
                        )}
                      </div>

                      {/* Mobile */}
                      <div className="space-y-1">
                        <Label htmlFor="signup-mobile" className="text-xs font-semibold text-gray-700">
                          Mobile Number *
                        </Label>
                        <Input 
                          id="signup-mobile"
                          placeholder="9876543210"
                          value={signupForm.mobile}
                          onChange={(e) => setSignupForm({...signupForm, mobile: e.target.value.replace(/\D/g, '').slice(0, 10)})}
                          required
                          className="border-2 border-gray-200 focus:border-orange-500 h-12 text-base"
                        />
                      </div>
                      
                      <Button
                        type="submit"
                        disabled={checkingProfileId || profileIdAvailable === false || (signupForm.profile_id.length >= 3 && profileIdAvailable === null)}
                        className="w-full bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white rounded-full py-6 text-lg font-bold shadow-lg mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {checkingProfileId ? 'Checking...' : '🎁 Free Account बनवा →'}
                      </Button>

                      <p className="text-center text-xs text-gray-500 mt-3">
                        Already have account? <a href="/auth" className="text-orange-600 font-semibold hover:underline">Login करा</a>
                      </p>
                    </form>
                  )}

                  {/* Step 2: Email, City, Password */}
                  {signupStep === 2 && (
                    <form onSubmit={handleSignupSubmit} className="space-y-4">
                      {/* Show selected profile info */}
                      <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                        <p className="text-xs text-green-600 font-medium">✅ तुमची Profile Link:</p>
                        <p className="text-sm font-bold text-green-700">designbiz.in/{signupForm.profile_id}</p>
                      </div>

                      {/* Email */}
                      <div className="space-y-1">
                        <Label htmlFor="signup-email" className="text-xs font-semibold text-gray-700">
                          Email *
                        </Label>
                        <Input 
                          id="signup-email"
                          type="email"
                          placeholder="your@email.com"
                          value={signupForm.email}
                          onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                          required
                          className="border-2 border-gray-200 focus:border-orange-500 h-12 text-base"
                        />
                      </div>

                      {/* City */}
                      <div className="space-y-1">
                        <Label htmlFor="signup-location" className="text-xs font-semibold text-gray-700">
                          शहर / City *
                        </Label>
                        <Input 
                          id="signup-location"
                          placeholder="उदा: Pune, Mumbai"
                          value={signupForm.location}
                          onChange={(e) => setSignupForm({...signupForm, location: e.target.value})}
                          required
                          className="border-2 border-gray-200 focus:border-orange-500 h-12 text-base"
                        />
                      </div>

                      {/* Password */}
                      <div className="space-y-1">
                        <Label htmlFor="signup-password" className="text-xs font-semibold text-gray-700">
                          Password * (min 6 characters)
                        </Label>
                        <div className="relative">
                          <Input 
                            id="signup-password"
                            type={showPassword ? 'text' : 'password'}
                            placeholder="••••••••"
                            value={signupForm.password}
                            onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                            required
                            minLength={6}
                            className="border-2 border-gray-200 focus:border-orange-500 h-12 text-base pr-10"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500"
                          >
                            {showPassword ? <EyeSlash className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex gap-2 mt-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setSignupStep(1)}
                          className="px-4 py-6 rounded-full"
                        >
                          ← Back
                        </Button>
                        <Button
                          type="submit"
                          disabled={loading}
                          className="flex-1 bg-gradient-to-r from-orange-500 to-pink-600 hover:from-orange-600 hover:to-pink-700 text-white rounded-full py-6 text-lg font-bold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {loading ? 'Creating...' : '🚀 Profile बनवा!'}
                        </Button>
                      </div>
                    </form>
                  )}
                </CardContent>
              </Card>
            </div>

          </div>
        </div>
      </div>

      {/* Problems Section */}
      <div className="bg-white py-16 md:py-20 border-y border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-red-100 text-red-700 border-0 mb-4 px-4 py-2 font-bold">
              समस्या
            </Badge>
            <h2 className="font-['Chivo'] text-4xl md:text-5xl font-black text-[#121212] mb-4">
              हे Problems तुम्हाला आहेत का?
            </h2>
            <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
              बहुतेक designers ला हेच problems आहेत. तुम्ही एकटे नाही!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problems.map((problem, i) => (
              <Card key={i} className="border-2 border-red-200 bg-red-50/50">
                <CardContent className="pt-6">
                  <div className="text-4xl mb-3">⚠️</div>
                  <h4 className="font-bold text-[#121212] text-lg mb-2">{problem.title}</h4>
                  <p className="text-[#6B7280] text-sm">{problem.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="flex justify-center mt-10">
            <Button
              size="default"
              onClick={() => navigate('/auth')}
              className="bg-green-600 hover:bg-green-700 text-white rounded-full font-bold px-6 py-4 text-base"
            >
              ✅ Solution मिळवा!
            </Button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
        <div className="text-center mb-12">
          <Badge className="bg-blue-100 text-blue-700 border-0 mb-4 px-4 py-2 font-bold">
            FEATURES
          </Badge>
          <h2 className="font-['Chivo'] text-4xl md:text-5xl font-black text-[#121212] mb-4">
            तुमच्या Business साठी
            <br />
            <span className="text-[#FF5A36]">सगळं काही एका ठिकाणी</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((feature, i) => {
            const Icon = feature.icon;
            return (
              <Card key={i} className="border-[#E5E7EB] hover:border-[#FF5A36] hover:shadow-xl transition-all group">
                <CardContent className="pt-6">
                  <div className="bg-gradient-to-br from-orange-100 to-pink-100 p-4 rounded-xl w-fit mb-4 group-hover:scale-110 transition-transform">
                    <Icon className="h-8 w-8 text-[#FF5A36]" weight="duotone" />
                  </div>
                  <h4 className="font-['Chivo'] text-xl font-bold text-[#121212] mb-3">
                    {feature.title}
                  </h4>
                  <p className="text-[#6B7280] leading-relaxed">
                    {feature.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Testimonials */}
      <div className="bg-gradient-to-br from-orange-50 to-pink-50 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Badge className="bg-yellow-100 text-yellow-800 border-0 mb-4 px-4 py-2 font-bold">
              SUCCESS STORIES
            </Badge>
            <h3 className="font-['Chivo'] text-4xl md:text-5xl font-black text-[#121212] mb-4">
              Designers Love Designbiz.in
            </h3>
            <p className="text-lg text-[#6B7280]">
              शेकडो designers ने आधीच transform केलं आहे!
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((t, i) => (
              <Card key={i} className="bg-white border-2 border-orange-200">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className="h-5 w-5 text-yellow-500 fill-yellow-500" weight="fill" />
                    ))}
                  </div>
                  <p className="text-[#121212] mb-4 italic leading-relaxed">"{t.text}"</p>
                  <div className="border-t border-[#E5E7EB] pt-4">
                    <p className="font-bold text-[#121212]">{t.name}</p>
                    <p className="text-sm text-[#6B7280] mb-2">{t.business}</p>
                    <Badge className="bg-green-100 text-green-800 border-0">
                      <TrendUp className="h-3 w-3 mr-1" weight="bold" /> {t.result}
                    </Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="bg-gradient-to-br from-[#FF5A36] via-[#E04828] to-purple-600 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-8">
            <div className="text-6xl mb-4">🚀</div>
            <h3 className="font-['Chivo'] text-4xl md:text-6xl font-black text-white mb-6">
              तुमची Profile बनवा,
              <br />आणि Business ला Grow करा!
            </h3>
            <p className="text-xl md:text-2xl text-white/90 mb-10">
              500+ designers ने आधीच सुरुवात केली आहे.
              <br />
              <span className="font-bold">आता तुमची वेळ आली आहे!</span>
            </p>
          </div>
          
          <div className="flex justify-center">
            <Button
              size="lg"
              onClick={() => navigate('/auth')}
              className="bg-white hover:bg-gray-100 text-[#FF5A36] rounded-full px-8 py-6 text-lg font-black shadow-2xl hover:scale-105 transition-all"
            >
              🎁 Free Profile बनवा <ArrowRight className="ml-2 h-5 w-5" weight="bold" />
            </Button>
          </div>
          
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 inline-block mt-6">
            <p className="text-white text-lg font-bold mb-1">
              Premium Plan: <span className="text-yellow-300">₹99/month</span>
            </p>
            <p className="text-white/80 text-sm">
              Enquiry Management • Invoices • Payment Tracking • Analytics
            </p>
          </div>
          
          <p className="text-white/90 text-sm mt-4">
            ✅ Profile FREE • ✅ Premium Features ₹99/month • ✅ कधीही Cancel करा
          </p>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-white border-t border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="col-span-2">
              <h4 className="font-['Chivo'] text-2xl font-black text-[#FF5A36] mb-4">
                Designbiz.in
              </h4>
              <p className="text-[#6B7280] mb-4 leading-relaxed">
                भारतातील designers साठी बनवलेलं #1 business management platform.
                तुमच्या design business ला organize आणि scale करा - सगळं एका ठिकाणी!
              </p>
              <div className="flex gap-2">
                <Badge className="bg-green-100 text-green-800 border-0">100% Marathi Support</Badge>
                <Badge className="bg-blue-100 text-blue-800 border-0">Made in India</Badge>
              </div>
            </div>
            <div>
              <h5 className="font-bold text-[#121212] mb-4">Quick Links</h5>
              <ul className="space-y-2 text-[#6B7280]">
                <li><a href="/auth" className="hover:text-[#FF5A36]">Free Profile बनवा</a></li>
                <li><a href="#features" className="hover:text-[#FF5A36]">Features</a></li>
                <li><a href="#testimonials" className="hover:text-[#FF5A36]">Success Stories</a></li>
              </ul>
            </div>
            <div>
              <h5 className="font-bold text-[#121212] mb-4">Support</h5>
              <ul className="space-y-2 text-[#6B7280]">
                <li><a href="#" className="hover:text-[#FF5A36]">Help Center</a></li>
                <li><a href="/auth" className="hover:text-[#FF5A36]">Login</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-[#E5E7EB] pt-8 text-center">
            <p className="text-[#6B7280]">
              © 2026 <span className="font-bold">Designbiz.in</span> | Made with ❤️ for Indian Designers
            </p>
          </div>
        </div>
      </footer>

    </div>
  );
};

export default LandingPage;
